<template>
  <section class="app">
    <input v-model="searchAddress" @input="fetchSuggestions" placeholder="Enter address or neighborhood"
      class="search-box">
    <ul v-if="addressSuggestions.length" class="suggestions">
      <li v-for="(suggestion, index) in addressSuggestions" :key="index" @click="selectAddress(suggestion)">
        {{ suggestion.place_name }}
      </li>
    </ul>
    <button @click="searchAndFlyToAddress" class="flyto-button">Search</button>
    <button @click="goBackToStory" class="story-button">BACK TO STORY</button>
    <button @click="toggleMethodology" class="method-button">?</button>
    <Methodology :isVisible="showMethodologyPopup" @close="showMethodologyPopup = false" />


    <div class="toggle-switch">
      <span class="label">1960</span>
      <label class="switch">
        <input type="checkbox" v-model="yearFilterChecked" @change="toggleYearFilter">
        <span class="slider round"></span>
      </label>
      <span class="label">1990</span>
    </div> <!-- Existing toggle and buttons -->
    <!-- <button @click="zoomToFirstLocation" class="flyto-button">Zoom to First Location</button>
      <button @click="zoomToSecondLocation" class="flyto-button">Zoom to 93 Pearl Street</button> -->
    <!-- Search box for address -->


    <div class="map-container" ref="mapContainer"></div>
  </section>

</template>

<script>
import mapboxgl from 'mapbox-gl';
import Papa from 'papaparse';
import 'mapbox-gl/dist/mapbox-gl.css';
import Methodology from '@/components/Methodology.vue';



export default {
  name: 'App',

  components: {
    Methodology
  },
  data() {
    return {
      searchAddress: '',
      addressSuggestions: [],
      showMethodologyPopup: false,  // This controls the visibility

      map: null,
      currentYearFilter: 1961,
      yearFilterChecked: false,
      transitioning: false,
      popup: new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
      }),
    };
  }, props: {
    isVisible: Boolean
  },
  methods: {
    toggleMethodology() {
      this.showMethodologyPopup = !this.showMethodologyPopup;
    },

    closePopup() {
      this.$emit('close');
    }
    ,
    goBackToStory() {
      this.$router.push('/home');
    },
    toggleYearFilter() {
      this.transitioning = true;
      setTimeout(() => {
        this.currentYearFilter = this.yearFilterChecked ? 1991 : 1961;
        this.loadAndParseCSV();
        this.transitioning = false;
      }, 500);
    },
    loadAndParseCSV() {
      Papa.parse(`${process.env.BASE_URL}buildingclasses_new.csv`, {
        download: true,
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const filteredData = result.data.filter(row => {
            const yearBuilt = parseInt(row['Year Built'], 10);
            return !isNaN(yearBuilt) && yearBuilt < this.currentYearFilter;
          });
          this.populateMapWithMarkers(filteredData);
        }
      });
    },
    populateMapWithMarkers(data) {
      if (this.map.getSource('buildings')) {
        this.map.removeLayer('buildings');
        this.map.removeSource('buildings');
      }

      const features = data.map(item => ({
        type: 'Feature',
        properties: {
          address: item['Property Address'],
          yearBuilt: parseInt(item['Year Built'], 10),
          RBA: parseFloat(item['RBA'])
        },
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(item.Longitude), parseFloat(item.Latitude)]
        }
      }));

      this.map.addSource('buildings', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features
        }
      });

      this.map.addLayer({
        id: 'buildings',
        type: 'circle',
        source: 'buildings',
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            15, [
              'step',
              ['get', 'RBA'],
              2,       // Default size for anything under 10,000 at zoom 15
              10000, 4,
              50000, 5,
              100000, 9,
              500000, 12,
              1000000, 14
            ],
            18, [
              'step',
              ['get', 'RBA'],
              3,       // Default size for anything under 10,000 at zoom 18
              10000, 5,
              50000, 7,
              100000, 15,
              500000, 20,
              1000000, 25
            ]
          ]
          ,
          'circle-color': [
            'case',
            ['<', ['get', 'yearBuilt'], 1961], '#870058',
            ['>=', ['get', 'yearBuilt'], 1961], '#36a39e',
            '#ccc' // Fallback color
          ],
          'circle-opacity': 0.3
        }
      });
    },
    handleHover(event) {
      const coordinates = event.features[0].geometry.coordinates.slice();
      const properties = event.features[0].properties;

      const description = `<strong>Address:</strong> ${properties.address}<br><strong>Year Built:</strong> ${properties.yearBuilt}<br><strong>Total Square Feet:</strong> ${properties.RBA}`;
      this.popup.setLngLat(coordinates)
        .setHTML(description)
        .addTo(this.map);
    },
    // zoomToFirstLocation() {
    //   this.map.flyTo({
    //     center: [-74.0083, 40.7077],
    //     zoom: 15,
    //     essential: true
    //   });
    // },

    // zoomToSecondLocation() {
    //   this.map.flyTo({
    //     center: [-74.009921, 40.704279],  // Long, Lat format for 93 Pearl Street
    //     zoom: 20,
    //     essential: true
    //   });
    // },
    calculateAndUpdateRBA() {
      const bounds = this.map.getBounds();
      const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
      const polygon = {
        type: 'Polygon',
        coordinates: [
          [
            [bbox[0], bbox[1]],
            [bbox[0], bbox[3]],
            [bbox[2], bbox[3]],
            [bbox[2], bbox[1]],
            [bbox[0], bbox[1]]  // Close the polygon
          ]
        ]
      };

      const features = this.map.queryRenderedFeatures({
        layers: ['buildings'],
        filter: ['all', ['within', polygon]]
      });

      const totalRBA = features.reduce((sum, feature) => sum + feature.properties.RBA, 0);
      console.log('Total RBA in current viewport:', totalRBA);
      this.$emit('update-rba', totalRBA);
    },
    fetchSuggestions() {
      if (!this.searchAddress.trim()) {
        this.addressSuggestions = [];
        return;
      }

      const bbox = "-74.25909,40.477399,-73.700181,40.917576";  // NYC bounding box
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(this.searchAddress)}.json?access_token=${mapboxgl.accessToken}&autocomplete=true&limit=5&bbox=${bbox}`;

      fetch(url)
        .then(response => response.json())
        .then(data => {
          this.addressSuggestions = data.features;
        })
        .catch(error => {
          console.error('Error fetching suggestions:', error);
          this.addressSuggestions = [];
        });
    },

    selectAddress(suggestion) {
      this.searchAddress = suggestion.place_name;
      this.addressSuggestions = [];
      // Check if the address includes any number
      const hasNumber = /\d/.test(suggestion.place_name);
      const zoomLevel = hasNumber ? 20 : 16; // Zoom to 20 if there's a number, otherwise 15

      this.map.flyTo({
        center: suggestion.center,
        zoom: zoomLevel,
        essential: true
      });
    },


  },
  mounted() {
    mapboxgl.accessToken = 'pk.eyJ1IjoiZWppa2lhIiwiYSI6ImNsbnhmcHhpdzBmamQycW11MTh0YTd5cDEifQ.3DLLa5EJ3mIiwzPdd02nPg';
    this.map = new mapboxgl.Map({
      container: this.$refs.mapContainer,
      style: 'mapbox://styles/mapbox/light-v10',
      center: [-73.927218, 40.728628],
      zoom: 10.6,
      minZoom: 10
    });

    this.map.on('load', () => {
      this.loadAndParseCSV();
    });
    this.map.on('mouseenter', 'buildings', (e) => {
      this.map.getCanvas().style.cursor = 'pointer';
      this.handleHover(e);
    });
    this.map.on('mouseleave', 'buildings', () => {
      this.map.getCanvas().style.cursor = '';
      this.popup.remove();
    });
    // Event listeners for map movement and zoom to update RBA
    this.map.on('moveend', this.calculateAndUpdateRBA);
    this.map.on('zoomend', this.calculateAndUpdateRBA);
  },
  beforeUnmount() {
    // Remove event listeners to prevent memory leaks
    this.map.off('moveend', this.calculateAndUpdateRBA);
    this.map.off('zoomend', this.calculateAndUpdateRBA);
  }
};


</script>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  /* Lower than suggestions */

}

.controls {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
  /* Ensure controls layer over the map */
}

/* #selection-box {
  position: absolute;
  background-color: rgba(135, 206, 250, 0.3);
  border: 2px solid #87cefa;
} */

.toggle-switch {
  position: absolute;
  top: 20px;
  left: 7%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  z-index: 1;
  background-color: rgba(244, 247, 249, 0.943);
  padding: 6px 6px;
  border-radius: 34px;
  font-family: 'Barlow Condensed', sans-serif;

  font-weight: 400;
  font-size: 15pt;


}

.label {
  font-size: 14px;
  color: black;
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
  margin: 0 10px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked+.slider {
  background-color: #2196F3;
}

input:checked+.slider:before {
  transform: translateX(26px);
}

.flyto-button {
  margin-left: 10px;
  padding: 5px 10px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 400;
  font-size: 14pt;
  position: absolute;
  top: 30px;
  left: 54.5%;
  z-index: 5000;
}

.flyto-button:hover {
  background-color: #1976D2;
}

.search-box {
  position: absolute;
  z-index: 5000;
  top: 30px;
  left: 43%;

  padding: 5px 10px;
  border: 2px solid #ccc;
  border-radius: 4px;
  width: 200px;
  /* Set width to fit the map width */
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 400;
  font-size: 12pt;
}

.suggestions {
  position: absolute;
  z-index: 5000;
  top: 60px;
  left: 43%;
  list-style: none;
  padding: 0;
  margin: 0;
  position: absolute;
  background: white;
  width: 250px;
  /* Match width with search box */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 11;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 400;
  font-size: 12pt;
  /* Higher than .controls to overlay on the search box */
}

.suggestions li {
  padding: 8px 10px;
  cursor: pointer;
  border-bottom: 1px solid #ccc;
}

.suggestions li:last-child {
  border-bottom: none;
}

.suggestions li:hover {
  background-color: #f8f8f8;
}

.app {
  overflow: visible;
  /* This ensures that children can overlay outside the parent's box */
}

.story-button {

  position: absolute;
  margin-top: 30px;
  left: 92%;
  /* Space above the button */
  padding: 5px 10px;
  background-color: white;
  /* Example blue color */
  color: #2196F3;
  border-color: #2196F3;
  border-radius: 4px;

  cursor: pointer;
  z-index: 7000;
  border-radius: 50px;
  font-family: 'Barlow Condensed', sans-serif;

  font-weight: 500;
  font-size: 14pt;

}


.story-button:hover {
  background-color: #70b3f7;
  /* Darker blue on hover */
}

.method-button {

  position: absolute;
  margin-top: 25px;
  left: 1%;
  text-align: center;
  width: 34px;
  /* Adjusted for 2px border: 30px + 2 * 2px border */
  height: 34px;
  /* Adjusted for 2px border: 30px + 2 * 2px border */
  background-color: white;
  /* Background color of the button */
  color: #2196F3;
  /* Text color */
  border: 2px solid #2196F3;
  /* Border color and width */
  border-radius: 50%;
  /* Makes the button round */
  cursor: pointer;
  z-index: 7000;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 500;
  font-size: 14pt;
  box-sizing: border-box;
  /* Includes padding and border in the element's size */
  display: flex;
  /* Ensures the content is centered */
  justify-content: center;
  /* Center content horizontally */
  align-items: center;
  /* Center content vertically */
  padding: 0;

}


.method-button:hover {
  background-color: #70b3f7;
  /* Darker blue on hover */
}
</style>
